import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { addMinutes, isAfter } from 'date-fns';
import { AsyncProcessingJob, AsyncProcessorContextType, CreateAsyncProcessingJob } from './AsyncProcessorContext.type';
import { Logger } from '../../utilities';

export const AsyncProcessorContext = createContext<AsyncProcessorContextType>({
  processingJobs: [],
  addJob: () => {},
  removeJob: () => {},
});

const ASYNC_GC_THRESHOLD_IN_MINUTES = 30;

export const AsyncProcessorProvider = ({ children }: { children: React.ReactNode }) => {
  const [processingJobs, setProcessingJobs] = useState<AsyncProcessingJob[]>([]);
  const intervalsRef = useRef<Record<string, NodeJS.Timeout>>({});

  const addJob = useCallback((job: CreateAsyncProcessingJob) => {
    setProcessingJobs(prev => {
      const currentDate = new Date();
      const alreadyHasJobById = prev.find(item => item.id === job.id);
      if (alreadyHasJobById) return prev;

      // right now the cleanup time is enforced but can be configured down the road
      return [
        ...prev,
        { ...job, startDate: currentDate, cleanupDate: addMinutes(currentDate, ASYNC_GC_THRESHOLD_IN_MINUTES) },
      ];
    });
  }, []);

  const removeJob = useCallback((id: string) => {
    setProcessingJobs(prev => prev.filter(job => job.id !== id));
    if (intervalsRef.current[id]) {
      clearInterval(intervalsRef.current[id]);
      delete intervalsRef.current[id];
    }
  }, []);

  useEffect(() => {
    processingJobs.forEach(job => {
      if (!intervalsRef.current[job.id]) {
        intervalsRef.current[job.id] = setInterval(async () => {
          try {
            const args = job.pollingConfig.pollingArgs || [];
            const result = await job.pollingConfig.pollingFunction(...args);
            job.onResult(result);

            const isAfterThreshold = isAfter(new Date(), job.cleanupDate);
            if (isAfterThreshold) {
              Logger.debug(
                `${job.id} is being cleaned up as threshold is surpassed(startDate: ${job.startDate} - cleanupDate: ${job.cleanupDate})`
              );
              removeJob(job.id);
            }
          } catch (error) {
            job.onError?.(error as Error);
          }
        }, job.pollingConfig.pollingInterval);
      }
    });

    const currentJobIds = new Set(processingJobs.map(job => job.id));
    Object.keys(intervalsRef.current).forEach(id => {
      if (!currentJobIds.has(id)) {
        clearInterval(intervalsRef.current[id]);
        delete intervalsRef.current[id];
      }
    });

    return () => {
      // I am disabling the exhaustive-deps because at the time of destruction, I want the current value of the intervals
      // to ensure that I do not introduce memory leaks. - Marcus Cantu
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const intervals = intervalsRef.current;
      Object.values(intervals).forEach(clearInterval);
    };
  }, [processingJobs, removeJob]);


  const value = useMemo(
    () => ({
      processingJobs,
      addJob,
      removeJob,
    }),
    [processingJobs, addJob, removeJob]
  );

  return <AsyncProcessorContext.Provider value={value}>{children}</AsyncProcessorContext.Provider>;
};

export const useAsyncProcessor = () => {
  const context = useContext(AsyncProcessorContext);
  if (!context) {
    throw new Error('useAsyncProcessor must be used within an AsyncProcessorProvider');
  }
  return context;
};
